import { MdlDialogService } from '@angular-mdl/core';
import { MdlSelectComponent } from '@angular-mdl/select';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, ViewEncapsulation, ElementRef, Output, EventEmitter} from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { cloneDeep, find, isEmpty, isEqual, isNil, sortBy } from 'lodash';
import { BehaviorSubject, Subscription, Subject } from 'rxjs';
import { flatMap, switchMap, map, withLatestFrom } from 'rxjs/operators';
import { comparators } from '../core/';
import { CovanceFilter } from '../covance-filter/covance-filter';
import { FilterFieldType, FilterFieldValue } from './covance-filter.models';
import { FilterNameDialogComponent, FilterNameDialogData, FilterNameDialogResult, FILTER_NAME_DIALOG_DATA } from './filter-name-dialog.component';
import { FiltersStorage, UserFilter } from './filters-storage';
export const selectedCovanceFilterName = 'selectedCovanceFilterName';

@Component({
    selector: 'covance-filter',
    template: require('./covance-filter.component.html'),
    styles: [require('./covance-filter.component.scss')],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CovanceFilterComponent implements OnInit, OnDestroy, OnChanges {
    @Input()
    public disableSelectorFilter: boolean;

    @Input()
    public fieldVals: FilterFieldValue[] = [];

    @Input()
    public siteId: number = null;

    @Input()
    public protocolId: number = null;

    @Input()
    public group: string;

    @Input()
    public set hideFilterExpandButton(value: boolean) {
        this.isFilterExpandButtonHidden = value;
        this.isFilterExpand = value;
    }

    @Input()
    public hideFiltersFields: boolean;

    @Input()
    public isFilterExpand: boolean;

    @Output()
    public filterChanged = new EventEmitter<UserFilter>();

    @ViewChild('selectFilter')
    public selectFilterComponent: MdlSelectComponent;

    @ViewChild('container')
    public container: ElementRef<HTMLDivElement>;

    public filterFieldType = FilterFieldType;
    public selectedFilter: UserFilter;
    public isFilterExpandButtonHidden = false;

    public filterSelectionNotification: Subject<any> = new Subject();

    private filters: UserFilter[] = [];

    private savedFilters: UserFilter[];

    private filterSubscription: Subscription;

    private filterGroup = new BehaviorSubject<string>(this.group);

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private dialogService: MdlDialogService,
        private changeDetectorRef: ChangeDetectorRef,
        private filter: CovanceFilter,
        private filterStorage: FiltersStorage
    ) { }

    public ngOnInit() {

        const selectedFilterName = this.route.queryParams.pipe(
            map(qp => qp[selectedCovanceFilterName] || null)
        ); 

        this.filterSubscription = this.filterStorage.isLoaded
            .pipe(
                flatMap(() => this.filterGroup),
                withLatestFrom(selectedFilterName),
                switchMap(([group, filterName]) => {
                    this.filters = cloneDeep(this.filterStorage.getFilters(group, this.siteId, this.protocolId));
                    this.selectedFilter = this.filters.find(f => f.name === filterName); 
                    this.savedFilters = cloneDeep(this.filters);
                    return this.filter.listen(this.fieldVals || []);
                }))
            .subscribe((queryParams: Params) => {
                if (this.selectedFilter) {
                    this.selectedFilter.params = queryParams;
                }

                this.updateSelect();

                this.changeDetectorRef.markForCheck();
            });
    }

    public ngOnChanges(changes: SimpleChanges) {
        if (changes['siteId'] || changes['protocolId'] || changes['fieldVals'] && changes['fieldVals'].previousValue !== this.fieldVals) {
            this.selectedFilter = null;
            this.filterGroup.next(this.group);
        }
    }

    public getContainerWidth() {
        return this.container && this.container.nativeElement && this.container.nativeElement.offsetWidth;
    }

    public ngOnDestroy() {
        this.filterSubscription.unsubscribe();
    }

    public onFilterChange(newSelectedFilter: UserFilter) {
        if (this.selectedFilter !== newSelectedFilter) {
            this.selectedFilter = newSelectedFilter;
            //EGU-2777
            this.filterSelectionNotification.next(true);

            let queryParams: Params = {};
            if (this.selectedFilter) {
                queryParams = {...this.selectedFilter.params };
                queryParams[selectedCovanceFilterName] = this.selectedFilter.name;
            }
            this.filterChanged.next(newSelectedFilter);
            this.router.navigate([], {
                queryParams: queryParams,
                relativeTo: this.route
            });
        }
    }

    public toggleFilter() {
        this.isFilterExpand = !this.isFilterExpand;
    }

    public isUnsavedFilter(filter: UserFilter) {
        if (!filter.id) {
            return true;
        }

        return this.isChangedFilter(filter);
    }

    public isEmptyFilter(filter: UserFilter): boolean {
        if (!filter) {
            return this.fieldVals ? !this.fieldVals.some(fieldVal => !isEmpty(fieldVal.queryParamValue)) : true;
        }
        return Object.keys(filter.params).length === 0;
    }

    public isChangedFilter(filter: UserFilter): boolean {
        const savedFilter = find(this.savedFilters, f => f.id === filter.id);
        const result = !isEqual(filter, savedFilter);
        return result;
    }

    public async save() {
        const savedFilter = await this.filterStorage.save(this.selectedFilter);

        const filter = find(this.savedFilters, (f: UserFilter) => f.id === this.selectedFilter.id);
        const index = this.savedFilters.indexOf(filter);
        this.savedFilters[index] = savedFilter;

        this.updateSelect();
    }

    public async create() {
        const dialogData: FilterNameDialogData = {
            formTitle: 'Create Filter',
            filterName: '',
            occupiedFilterNames: this.getOccupiedFilterNames()
        };

        const dialogResult = await this.showFilterNameDialog(dialogData);

        if (dialogResult.saveChanges) {
            const filter = new UserFilter();
            filter.siteId = this.siteId;
            filter.protocolId = this.protocolId;
            filter.name = dialogResult.filterName;
            filter.group = this.group;
            filter.params = {};

            this.fieldVals.forEach(fieldVal => {
                if (fieldVal.def.queryParam && !isNil(fieldVal.queryParamValue)) {
                    filter.params[fieldVal.def.queryParam] = fieldVal.queryParamValue;
                }
            });

            const savedFilter = await this.filterStorage.save(filter);

            this.filters.push(savedFilter);
            this.savedFilters.push(savedFilter);
            this.selectedFilter = savedFilter;
            this.updateSelect();
        }
    }

    public async edit() {
        const dialogData: FilterNameDialogData = {
            formTitle: 'Edit Filter',
            filterName: this.selectedFilter.name,
            occupiedFilterNames: this.getOccupiedFilterNames(this.selectedFilter.id),
        };

        const dialogResult = await this.showFilterNameDialog(dialogData);

        if (dialogResult.saveChanges) {
            this.selectedFilter.name = dialogResult.filterName;
            this.updateSelect();
        }
    }

    public undo(filter: UserFilter) {
        const savedFilter = find(this.savedFilters, f => f.id === filter.id);
        const undoFilter = cloneDeep(savedFilter);
        const index = this.filters.indexOf(filter);
        this.filters[index] = undoFilter;
        this.onFilterChange(undoFilter);
    }

    public async showFilterNameDialog(dialogData: FilterNameDialogData): Promise<FilterNameDialogResult> {
        const dialog = await this.dialogService.showCustomDialog({
            component: FilterNameDialogComponent,
            providers: [
                {
                    provide: FILTER_NAME_DIALOG_DATA,
                    useValue: dialogData
                }
            ],
            isModal: true
        }).toPromise();

        try {
            const result = await dialog.onHide().toPromise<FilterNameDialogResult>();
            return result;
        } catch  {
            return { saveChanges: false };
        }
    }

    public async delete() {
        const confirmed = await this.dialogService
            .confirm('Are you sure you want to delete the filter?', 'Cancel', 'OK')
            .toPromise().then(() => true).catch(() => false);

        if (confirmed) {
            if (!this.selectedFilter.id) {
                this.deleteFilter();
            } else {
                await this.filterStorage.delete(this.selectedFilter.id);
                this.savedFilters = this.savedFilters.filter(f => f.id !== this.selectedFilter.id);
                this.deleteFilter();
            }
        }
    }

    public getVisibleFieldVals() {
        if (!this.fieldVals) {
            return [];
        }
        const visibleFilters = this.fieldVals.filter(f => f.def.type !== FilterFieldType.External);
        const fieldVals = this.isFilterExpand ? visibleFilters : visibleFilters.filter(f => f.def.isPinned || !isEmpty(f.queryParamValue));
        const fieldValsWithOrder = sortBy(fieldVals.filter(val => !isNaN(val.def.order)), (val: FilterFieldValue) => val.def.order);
        const fieldValsWithoutOrder = fieldVals.filter(val => isNaN(val.def.order));

        return [...fieldValsWithOrder, ...fieldValsWithoutOrder];
    }

    public getFilters() {
        return this.filters.sort((a, b) => comparators.stringComparator(a.name, b.name));
    }

    public getFilterName(filter: UserFilter) {
        if (!filter) {
            return '';
        }
        if (this.isUnsavedFilter(filter)) {
            return filter.name + ' *';
        }
        return filter.name;
    }

    public getDisabledAttr(selectedFilter: boolean) {
        return selectedFilter ? null : 'disabled';
    }

    private getOccupiedFilterNames(filterId?: string) {
        const filterNames = this.filters
            .filter(f => f.id !== filterId)
            .map(f => f.name);

        const savedFilterNames = this.savedFilters
            .filter(f => f.id !== filterId)
            .map(f => f.name);

        return [...filterNames, ...savedFilterNames];
    }

    // hack for updating selected value in mdl-select
    private updateSelect() {
        if (this.selectedFilter) {
            const index = this.filters.indexOf(this.selectedFilter);
            this.selectedFilter = cloneDeep(this.selectedFilter);
            this.filters[index] = this.selectedFilter;
            this.selectFilterComponent.writeValue(this.selectedFilter);
            this.selectFilterComponent.isDirty();
        }
    }

    private deleteFilter() {
        this.filters = this.filters.filter(f => f.id !== this.selectedFilter.id);
        this.onFilterChange(null);
        this.selectFilterComponent.reset();
    }
}
